import { supabase } from '../lib/supabaseClient';
import { track, Events } from './analytics';

export interface VybeLimits {
  create: {
    monthly: number;
    tokens: number;
    used: number;
    canUse: boolean;
    remaining: number;
    periodStart: number;
  };
  apply: {
    monthly: number;
    tokens: number;
    used: number;
    canUse: boolean;
    remaining: number;
    periodStart: number;
  };
  accepted: {
    monthly: number;
    tokens: number;
    used: number;
    canUse: boolean;
    remaining: number;
    periodStart: number;
  };
  isSubscribed: boolean;
}

const FREE_LIMITS = {
  create: 1
};

const SUBSCRIBED_LIMITS = {
  create: 3
};

export const checkVybeLimits = async (userId: string, isSubscribed: boolean): Promise<VybeLimits> => {
  try {
    const { data, error } = await supabase.rpc('check_and_reset_monthly_limits', {
      user_id: userId,
      is_subscribed: isSubscribed
    });

    if (error) throw error;

    const limits = data[0];

    // Use the dynamic monthly limits from the database (not hardcoded)
    // This accounts for first month having 0 allowance for free tier
    const createRemaining = Math.max(0, limits.monthly_create_limit - limits.current_created) + limits.token_create;

    return {
      create: {
        monthly: limits.monthly_create_limit,
        tokens: limits.token_create,
        used: limits.current_created,
        canUse: createRemaining > 0,
        remaining: createRemaining,
        periodStart: limits.period_tokens_create_start || 0
      },
      apply: {
        monthly: 999,
        tokens: 0,
        used: 0,
        canUse: true,
        remaining: 999,
        periodStart: 0
      },
      accepted: {
        monthly: 999,
        tokens: 0,
        used: 0,
        canUse: true,
        remaining: 999,
        periodStart: 0
      },
      isSubscribed
    };
  } catch (error) {
    console.error('Error checking Vybe limits:', error);
    const monthlyLimits = isSubscribed ? SUBSCRIBED_LIMITS : FREE_LIMITS;
    return {
      create: { monthly: monthlyLimits.create, tokens: 0, used: 0, canUse: false, remaining: 0, periodStart: 0 },
      apply: { monthly: 999, tokens: 0, used: 0, canUse: true, remaining: 999, periodStart: 0 },
      accepted: { monthly: 999, tokens: 0, used: 0, canUse: true, remaining: 999, periodStart: 0 },
      isSubscribed
    };
  }
};

export const canCreateVybe = async (userId: string, isSubscribed: boolean): Promise<{ canCreate: boolean; reason?: string }> => {
  const limits = await checkVybeLimits(userId, isSubscribed);

  if (limits.create.canUse) {
    return { canCreate: true };
  }

  if (isSubscribed) {
    return {
      canCreate: false,
      reason: `You've hosted ${limits.create.monthly} Vybes this month! 🎉\n\nYour tokens refresh next month, or grab a Top-Up for £2.49 to keep the momentum going.`
    };
  }

  return {
    canCreate: false,
    reason: `You've used your free Host token this month! ✨\n\nUpgrade or add a Top-Up to keep creating amazing Vybes.`
  };
};

export const canApplyToVybe = async (_userId: string, _isSubscribed: boolean): Promise<{ canApply: boolean; reason?: string }> => {
  // Join requests are NO LONGER token-limited
  // They are controlled by:
  // 1. Anti-spam rate limiting (handled separately)
  // 2. Rating + reliability system
  return { canApply: true };
};

// Helper to generate idempotency key
const generateIdempotencyKey = (userId: string, operation: string): string => {
  return `${userId}_${operation}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const consumeVybeCreateLimit = async (
  userId: string,
  isSubscribed: boolean,
  idempotencyKey?: string
): Promise<{ success: boolean; reason?: string }> => {
  try {
    const key = idempotencyKey || generateIdempotencyKey(userId, 'create');

    const { data, error } = await supabase.rpc('atomic_consume_create_token', {
      user_id: userId,
      is_subscribed: isSubscribed,
      idempotency_key: key
    });

    if (error) {
      console.error('Error consuming Vybe create limit:', error);
      return { success: false, reason: 'Failed to consume token' };
    }

    const result = data as { success: boolean; reason?: string };

    if (result.success) {
      track(Events.TokenConsumed, {
        userId,
        tokenType: 'create',
        isSubscribed,
        timestamp: new Date().toISOString()
      });
    } else {
      track(Events.TokenDeclined, {
        userId,
        tokenType: 'create',
        reason: result.reason,
        isSubscribed,
        timestamp: new Date().toISOString()
      });
      track(Events.AttemptedCreateBlocked, {
        userId,
        reason: result.reason,
        timestamp: new Date().toISOString()
      });
    }

    return result;
  } catch (error) {
    console.error('Error consuming Vybe create limit:', error);
    return { success: false, reason: 'Failed to consume token' };
  }
};

export const consumeVybeApplyLimit = async (
  _userId: string,
  _isSubscribed: boolean,
  _idempotencyKey?: string
): Promise<{ success: boolean; reason?: string }> => {
  // NO TOKEN CONSUMPTION for join requests
  // Database function now always returns success
  // Anti-spam is handled separately in the application flow
  return { success: true };
};

export const trackVybeAcceptance = async (_userId: string): Promise<{ success: boolean; reason?: string }> => {
  // NO TOKEN TRACKING for acceptance
  // Users can be accepted into unlimited Vybes
  // Quality is controlled by rating + reliability system, not tokens
  return { success: true };
};

export const refundVybeAcceptance = async (_userId: string): Promise<boolean> => {
  // NO-OP: Acceptance tokens are no longer tracked
  return true;
};

export const addPurchasedTokens = async (
  userId: string,
  createTokens: number = 1
): Promise<boolean> => {
  try {
    // Only add create tokens - apply and accepted tokens are no longer used
    const { error } = await supabase.rpc('add_purchased_tokens', {
      user_id: userId,
      create_tokens: createTokens,
      apply_tokens: 0,
      accepted_tokens: 0
    });
    return !error;
  } catch (error) {
    console.error('Error adding purchased tokens:', error);
    return false;
  }
};

export const getLimitErrorMessage = (type: 'create', isSubscribed: boolean): string => {
  const limits = isSubscribed ? SUBSCRIBED_LIMITS : FREE_LIMITS;

  return isSubscribed
    ? `You've reached your monthly limit of ${limits.create} Vybes created. Add a Top-Up for £2.49.`
    : `You've reached your free Vybe limit this month. Subscribe or add a Top-Up to continue connecting.`;
};

// ============================================================================
// WRAPPER FUNCTIONS WITH AUTO-REFRESH
// ============================================================================
// These wrappers automatically refresh user state after token operations.
// Always use these wrappers instead of calling the base functions directly.

export const consumeVybeCreateLimitWithRefresh = async (
  userId: string,
  isSubscribed: boolean,
  refreshUser: () => Promise<void>,
  idempotencyKey?: string
): Promise<{ success: boolean; reason?: string }> => {
  const result = await consumeVybeCreateLimit(userId, isSubscribed, idempotencyKey);
  if (result.success) {
    await refreshUser();
  }
  return result;
};

export const consumeVybeApplyLimitWithRefresh = async (
  _userId: string,
  _isSubscribed: boolean,
  _refreshUser: () => Promise<void>,
  _idempotencyKey?: string
): Promise<{ success: boolean; reason?: string }> => {
  // NO-OP: Apply tokens are no longer tracked
  return { success: true };
};

export const refundVybeCreateTokenWithRefresh = async (
  userId: string,
  refreshUser: () => Promise<void>
): Promise<void> => {
  await supabase.rpc('refund_vybe_create_token', { user_id: userId });
  await refreshUser();
};

export const refundVybeApplyTokenWithRefresh = async (
  _userId: string,
  _refreshUser: () => Promise<void>
): Promise<void> => {
  // NO-OP: Apply tokens are no longer tracked
  return;
};

export const addPurchasedTokensWithRefresh = async (
  userId: string,
  refreshUser: () => Promise<void>,
  createTokens: number = 1
): Promise<boolean> => {
  const success = await addPurchasedTokens(userId, createTokens);
  if (success) {
    await refreshUser();
  }
  return success;
};
